import { Copy, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { copyText } from "../../../lib/utils";
import { formatDateTime } from "../../../lib/baseline/format";
import type { ParsedEvent } from "../../../types/domain";

export function RawView({ events }: { events: ParsedEvent[] }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return events;
    return events.filter((event) =>
      [event.timestamp, event.subjectCode, event.actionCode, event.triggerCode, event.rawRow, event.title]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [events, query]);

  return (
    <div className="space-y-4">
      <label className="relative block">
        <span className="sr-only">Search raw rows</span>
        <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" aria-hidden />
        <Input className="pl-9" placeholder="Search raw rows" value={query} onChange={(event) => setQuery(event.target.value)} />
      </label>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full min-w-[920px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Timestamp</th>
              <th className="px-3 py-2">Subject</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Trigger</th>
              <th className="px-3 py-2">Details</th>
              <th className="px-3 py-2">Raw row</th>
              <th className="px-3 py-2">Copy</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.slice(0, 250).map((event) => (
              <tr key={event.eventId}>
                <td className="px-3 py-2">{formatDateTime(event.timestamp)}</td>
                <td className="px-3 py-2">{event.subjectCode}</td>
                <td className="px-3 py-2">{event.actionCode}</td>
                <td className="px-3 py-2">{event.triggerCode}</td>
                <td className="px-3 py-2">{event.detailEntries.map((detail) => `${detail.code}=${detail.rawValue}`).join(", ")}</td>
                <td className="max-w-xl truncate px-3 py-2 font-mono text-xs">{event.rawRow}</td>
                <td className="px-3 py-2">
                  <Button aria-label="Copy raw row" size="icon" type="button" variant="ghost" onClick={() => copyText(event.rawRow)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length > 250 ? (
        <p className="text-sm text-slate-600">Showing 250 of {filtered.length.toLocaleString()} matching rows. Search to narrow the raw view.</p>
      ) : null}
    </div>
  );
}
